import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { Grid } from '@mui/system';
import {
  ANALYST,
  COMPANY,
  COMPANY_DASHBOARD_ROLES,
  COMPANY_USER,
  COMPANY_USER_VALUE,
  FUND,
  FUND_ADMIN_VALUE,
  USER_ROLES,
} from 'common/constants/user';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { USER_PERMISSIONS_TEST_IDS } from 'pages/UserPermissions/UserPermissions.testIds';
import { InviteUserLedgerProps } from './InviteUserLedger.props';
import CompanyFundSelect from '../CompanyFundSelect';
import { inviteUserLedgerClasses, InviteUserLedgerRoot } from './InviteUserLedger.styles';
import { useInviteUserForm } from './useInviteUserForm';

const InviteUserLedger = ({
  dialogUserData,
  handleDialogClose,
  handleAddUserToFirm,
  isCompanyInvite = false,
}: InviteUserLedgerProps) => {
  const [{ companyList, fundList }] = useStore() as UseStoreValues;

  const {
    formState,
    roleType,
    firmItem,
    isSubmitting,
    submitError,
    isSubmitButtonDisabled,
    handleInputChange,
    handleCheckboxChange,
    handleRoleToggle,
    handleSelect,
    handleSubmit,
  } = useInviteUserForm({
    handleAddUserToFirm,
    isCompanyInvite,
    companyList,
    fundList,
  });

  if (!dialogUserData.open) {
    return null;
  }

  const rolesToDisplay = isCompanyInvite ? COMPANY_DASHBOARD_ROLES : USER_ROLES;
  const showCompanyFundSelect =
    !isCompanyInvite && [COMPANY_USER_VALUE, FUND_ADMIN_VALUE].includes(roleType);

  return (
    <InviteUserLedgerRoot
      onSubmit={handleSubmit}
      id="invite-user"
      data-testid={USER_PERMISSIONS_TEST_IDS.inviteFormSubmitButton}
    >
      {isSubmitting && <LinearProgress />}

      {submitError && (
        <Alert severity="error" data-testid={USER_PERMISSIONS_TEST_IDS.inviteFormSubmitError}>
          {submitError}
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid size={{ xs: 6 }}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            First Name
          </FormLabel>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="John"
            name="firstName"
            slotProps={{ htmlInput: { 'data-testid': USER_PERMISSIONS_TEST_IDS.firstNameInput } }}
            variant="outlined"
            value={formState.values.firstName}
            onChange={handleInputChange}
            error={formState.touched.firstName && !!formState.errors?.firstName}
            helperText={formState.touched.firstName && formState.errors?.firstName?.[0]}
          />
        </Grid>

        <Grid size={{ xs: 6 }}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            Last Name
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="Doe"
            name="lastName"
            slotProps={{ htmlInput: { 'data-testid': USER_PERMISSIONS_TEST_IDS.lastNameInput } }}
            variant="outlined"
            value={formState.values.lastName}
            onChange={handleInputChange}
            error={formState.touched.lastName && !!formState.errors?.lastName}
            helperText={formState.touched.lastName && formState.errors?.lastName?.[0]}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            Email
          </FormLabel>
          <TextField
            fullWidth
            size="small"
            placeholder="john@doe.com"
            name="email"
            slotProps={{ htmlInput: { 'data-testid': USER_PERMISSIONS_TEST_IDS.emailInput } }}
            variant="outlined"
            value={formState.values.email}
            onChange={handleInputChange}
            error={formState.touched.email && !!formState.errors?.email}
            helperText={formState.touched.email && formState.errors?.email?.[0]}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
            Define Role
          </FormLabel>
          <FormControl fullWidth size="small">
            <Select
              id={USER_PERMISSIONS_TEST_IDS.roleSelect}
              data-testid={USER_PERMISSIONS_TEST_IDS.roleSelect}
              value={roleType}
              variant="outlined"
              onChange={event => handleRoleToggle(Number(event.target.value))}
            >
              {Object.values(rolesToDisplay).map(r => (
                <MenuItem
                  key={r.value}
                  value={r.value}
                  data-testid={r.label === ANALYST ? USER_PERMISSIONS_TEST_IDS.analystRoleButton : undefined}
                >
                  {r.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid size={{ xs: 12, md: 6 }}>
          {showCompanyFundSelect && (
            <>
              <FormLabel component="legend" classes={{ root: inviteUserLedgerClasses.boldLabel }}>
                {`Select ${roleType === COMPANY_USER_VALUE ? COMPANY : FUND}`}
              </FormLabel>

              {roleType === FUND_ADMIN_VALUE && (
                <CompanyFundSelect
                  value={firmItem.itemId as number}
                  handleSelect={handleSelect}
                  itemType={FUND}
                  size="small"
                  variant="outlined"
                  itemList={
                    fundList?.map(fund => ({
                      id: fund.id as number,
                      name: fund.name ?? '',
                    })) ?? []
                  }
                />
              )}

              {roleType === COMPANY_USER_VALUE && (
                <CompanyFundSelect
                  value={firmItem.itemId as number}
                  handleSelect={handleSelect}
                  itemType={COMPANY}
                  size="small"
                  variant="outlined"
                  itemList={
                    companyList?.map(company => ({
                      id: company.company_id,
                      name: company.company_name ?? '',
                    })) ?? []
                  }
                />
              )}
            </>
          )}
        </Grid>

        {isCompanyInvite && (
          <>
            {roleType === USER_ROLES[COMPANY_USER].value && (
              <Grid size={{ xs: 12 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formState.values.isPrimaryContact || false}
                      onChange={handleCheckboxChange}
                      name="isPrimaryContact"
                      color="primary"
                    />
                  }
                  label="Primary Contact"
                />
              </Grid>
            )}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formState.values.sendActivationEmail || false}
                    onChange={handleCheckboxChange}
                    name="sendActivationEmail"
                    color="primary"
                  />
                }
                label="Send Activation Email"
              />
            </Grid>
          </>
        )}
      </Grid>

      <div className={inviteUserLedgerClasses.buttonGroup}>
        <Button variant="outlined" color="primary" onClick={handleDialogClose} sx={{ mr: 2 }}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" disabled={isSubmitButtonDisabled} type="submit">
          Add User
        </Button>
      </div>
    </InviteUserLedgerRoot>
  );
};

export default InviteUserLedger;
